import type {
  ElakeArvioModel,
  PensionCalculationResult,
} from "../../types/ElakeArvioModel";
import type { CalculationContext } from "./calculationContext";
import { runAccumulation } from "./accumulation/runAccumulation";

export type { CalculationContext } from "./calculationContext";

export function calculatePension(
  model: ElakeArvioModel,
  context: CalculationContext,
): PensionCalculationResult {
  if (model.income < 0 || model.accruedPension < 0) {
    throw new Error("Wrong income or accruedPension input");
  }
  if (model.accruedPension < 0) {
    model.accruedPension = 0;
  }

  const acc = runAccumulation(model, context);
  let pension = acc.pension;
  let income = acc.income;
  let pensionYear = acc.pensionYear;
  const pensionMonth = acc.pensionMonth;
  const teiNextYear = acc.teiNextYear;
  const startPriceLevel = acc.startPriceLevel;
  const endPriceLevel = acc.endPriceLevel;
  const exceptionIndexing = acc.exceptionIndexing;

  // lykkäyskorotus ja loput karttumat alimman vanhuuseläkeiän jälkeen
  let delayIncrease =
    model.birthYear < 1954
      ? (model.pensionAgeYear - context.lowestPensionAgeYear - 2) * 12 +
        (model.pensionAgeMonth + model.birthMonth)
      : (model.pensionAgeYear - context.lowestPensionAgeYear) * 12 +
        (model.pensionAgeMonth - context.lowestPensionAgeMonth);
  if (delayIncrease < 0) {
    delayIncrease = 0;
  }

  let pensionWithoutDelayIncrease = pension;
  pension = pension * (1 + delayIncrease * 0.004);

  // elinaikakerroin
  pension *= context.lifetimeFactor;
  pensionWithoutDelayIncrease *= context.lifetimeFactor;

  const priceLevelIncrease = endPriceLevel / startPriceLevel;

  let startYear = context.calculationYear;
  if (context.calculationMonth === 12) {
    startYear += 1;
  }

  if (startYear === pensionYear) {
    // jos jää samana vuonna eläkkeelle, nyt ei tehdä mitään, eläkekarttuma on korotettu oikeaan tasoon
  } else if (!model.priceConversion) {
    // valittuna nimellinen hintataso eli ei diskonttausta
  } else {
    pension /= priceLevelIncrease;
    income /= priceLevelIncrease;
    pensionWithoutDelayIncrease /= priceLevelIncrease;
  }

  if (exceptionIndexing) {
    pensionYear += 1;
  }

  if (pensionYear < startYear) {
    pensionYear = startYear;
  }

  return {
    pension,
    pensionYear,
    pensionMonth,
    income,
    pensionWithoutIncreaseForLateRetirement: pensionWithoutDelayIncrease,
    pensionNextYear: pension * teiNextYear,
    priceLevelIncrease,
  };
}
